import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Highcharts from 'highcharts';
import type { SeriesLineOptions, YAxisOptions, XAxisOptions, Options, PointMarkerOptionsObject } from 'highcharts';
import { formatDate } from 'shared/utils/time/format-date';
import type { ILinearChartOptionsBuilderData, TTooltipOneSeriesData } from 'components/insights/linear-chart/types';
import { ELineStyle } from 'components/insights/linear-chart/types';

dayjs.extend(utc);

const SATURDAY = 6;
const WEEKEND_BAND_COLOR = '#f4f5f7';

export class LinearChartOptionsBuilder {
  private colors = [
    '#2caffe',
    '#544fc5',
    '#00e272',
    '#fe6a35',
    '#6b8abc',
    '#d568fb',
    '#2ee0ca',
    '#fa4b42',
    '#feb56a',
    '#91e8e1',
  ];

  constructor(private readonly data: ILinearChartOptionsBuilderData) {}

  get options(): Highcharts.Options {
    return {
      ...this.baseOptions,
      xAxis: this.xAxisOption,
      yAxis: this.yAxisOption,
      series: this.seriesOption,
      plotOptions: this.plotOptions,
      tooltip: this.tooltip,
    };
  }

  private get colorsForComparison() {
    return this.colors.map((color) => new Highcharts.Color(color).setOpacity(0.4).get());
  }

  private get baseOptions(): Options {
    return {
      title: undefined,
      credits: {
        enabled: false,
      },
    };
  }

  private get xAxisOption(): XAxisOptions {
    return {
      type: 'datetime',
      crosshair: true,
      plotBands: this.weekendPlotBands,
    };
  }

  private get weekendPlotBands(): Highcharts.XAxisPlotBandsOptions[] {
    if (!this.data.highlightWeekends) {
      return [];
    }

    const range = this.getTimeRange();
    if (!range) {
      return [];
    }

    const bands: Highcharts.XAxisPlotBandsOptions[] = [];
    const start = dayjs.utc(range.min).startOf('day');
    const daysSinceSaturday = (start.day() - SATURDAY + 7) % 7;
    let weekendStart = start.subtract(daysSinceSaturday, 'day');

    while (weekendStart.valueOf() <= range.max) {
      bands.push({
        from: weekendStart.valueOf(),
        to: weekendStart.add(2, 'day').valueOf(),
        color: WEEKEND_BAND_COLOR,
        zIndex: 0,
      });

      weekendStart = weekendStart.add(1, 'week');
    }
    return bands;
  }

  private getTimeRange(): { min: number; max: number } | null {
    const timestamps = this.data.mainSeries
      .flatMap((series) => series.data.map(({ x }) => (typeof x === 'number' ? x : Date.parse(x))))
      .filter(Number.isFinite);

    if (timestamps.length === 0) {
      return null;
    }

    return {
      min: timestamps.reduce((a, b) => Math.min(a, b)),
      max: timestamps.reduce((a, b) => Math.max(a, b)),
    };
  }

  private get yAxisOption(): YAxisOptions[] {
    const yAxis: YAxisOptions[] = [
      {
        title: this.data.yAxisTitle ? { text: this.data.yAxisTitle } : undefined,
      },
    ];

    if (this.data.withOppositeYAxis) {
      yAxis.push({
        title: this.data.yAxisOppositeTitle ? { text: this.data.yAxisOppositeTitle } : undefined,
        opposite: true,
      });
    }

    return yAxis;
  }

  private get seriesOption(): SeriesLineOptions[] {
    return [...this.comparisonSeriesOptions, ...this.mainSeriesOptions];
  }

  private get mainSeriesOptions(): SeriesLineOptions[] {
    const marker: PointMarkerOptionsObject = {
      radius: 3,
      symbol: 'circle',
    };

    return this.data.mainSeries.map((series, idx) => {
      const { onPointClick } = series;

      return {
        type: 'line',
        name: series.name,
        data: series.data.map((seriesData) => ({ ...seriesData, unit: series.unit })),
        lineWidth: 3,
        yAxis: series.relatedToOppositeYAxis ? 1 : 0,
        color: this.colors[idx] || undefined,
        marker,
        cursor: onPointClick ? 'pointer' : undefined,
        point: onPointClick
          ? {
              events: {
                click: function () {
                  onPointClick({ x: Number(this.x) });
                },
              },
            }
          : undefined,
        pointPlacement: 'on',
      };
    });
  }

  private get comparisonSeriesOptions(): SeriesLineOptions[] {
    const marker: PointMarkerOptionsObject = {
      enabled: false,
      states: {
        hover: {
          enabled: false,
        },
      },
    };

    if (!this.data.comparisonSeries || this.data.comparisonSeries.length === 0) {
      return [];
    }

    return this.data.comparisonSeries.map((series, seriesIndex) => {
      const currentMainSeries = this.data.mainSeries[seriesIndex];
      return {
        type: 'line',
        name: `${series.name}_comparison`,
        dashStyle: this.data.comparisonLineStyle ?? ELineStyle.Solid,
        keys: ['x', 'y', 'comparisonDate'],
        data: series.data.map((seriesData, dataIndex) => ({
          x: currentMainSeries.data?.[dataIndex].x,
          y: seriesData.y,
          comparisonDate: seriesData.x,
          unit: currentMainSeries.unit,
        })),
        yAxis: series.relatedToOppositeYAxis ? 1 : 0,
        showInLegend: false,
        color: this.colorsForComparison[seriesIndex] ?? undefined,
        marker,
      };
    });
  }

  private get plotOptions(): Highcharts.PlotOptions {
    return {
      series: {
        events: {
          hide: function (event) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const name = event.target?.name ?? '';
            const comparisonName = `${name}_comparison`;

            const comparisonSeries = this.chart.series.find((s) => s.name === comparisonName);
            if (comparisonSeries) {
              comparisonSeries.hide();
            }
          },
          show: function (event) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const name = event.target?.name ?? '';
            const comparisonName = `${name}_comparison`;

            const comparisonSeries = this.chart.series.find((s) => s.name === comparisonName);

            if (comparisonSeries && this.visible) {
              comparisonSeries.show();
            }
          },
        },
      },
    };
  }

  private get tooltip(): Highcharts.TooltipOptions {
    return {
      shared: true,
      formatter: function () {
        const date = formatDate(new Date(this.x));
        let comparisonDate: string | undefined = undefined;
        const data: TTooltipOneSeriesData = {};
        const comparisonData: TTooltipOneSeriesData = {};

        let tooltipText = `<b>${date}</b></br>`;
        this.series.chart.series.forEach(function (series) {
          if (series.name.includes('_comparison')) {
            const realName = series.name.replace('_comparison', '');
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            comparisonDate = series.data[this.point.index]?.comparisonDate;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const unit = series.data[this.point.index].unit ?? '';
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const value = series.data[this.point.index].y ?? null;

            comparisonData[realName] = { value, unit };
          } else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const unit = series.data[this.point.index].unit ?? '';
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const value = series.data[this.point.index].y ?? null;

            data[series.name] = { value, unit };
          }
        }, this);

        Object.keys(data).forEach((key) => {
          const series = data[key];

          tooltipText += `<span>${key}: <b>${series.value}</b> ${series.unit}</span></br>`;
        });

        if (comparisonDate) {
          tooltipText += `</br><b style="color: var(--gray-7)">${comparisonDate}</b></br>`;
          Object.keys(data).forEach((key) => {
            const comparisonSeries = comparisonData[key];

            if (comparisonSeries.value !== null) {
              tooltipText += `<span style="color: var(--gray-7)">${key}: <b>${comparisonSeries.value}</b> ${comparisonSeries.unit}</span></br>`;
            }
          });
        }

        return tooltipText;
      },
    };
  }
}
